import { analytics } from '@mssgme/analytics';
import { omit } from 'lodash';

import { api, checkout } from '../actions';
import sift from 'sift';

// for mobile app do not send email to analytics
function isAppTrackingAuthorized(state) {
    return process.REACT_APP_TARGET !== 'mobile' || state.ui.appTrackingStatus === 'authorized';
}

const eventsMap = {
    [api.auth.login.successType]: () => {
        analytics.trackEvent('login', { method: 'email' });
    },
    [api.users.create.successType]: () => {
        analytics.trackEvent('sign_up', { method: 'email' });
    },
    [api.users.create.failureType]: (action) => {
        analytics.trackEvent('Sign up error', { error: omit(action.error, ['stack', 'json']) });
    },
    [api.websites.update.successType]: (action, prevState) => {
        const website = prevState.websites.find(sift({ _id: action.websiteId }));
        if (!website?.enabled && action.payload.enabled) {
            analytics.trackEvent('website_published');
        }
    },
    [api.listenSubscriptionCreated.type]: (action) => {
        analytics.trackEvent('subscription_created', action.payload);
    },
    [api.billing.subscription.cancel.successType]: () => {
        analytics.trackEvent('subscription_cancelled');
    },
    [api.users.me.successType]: (action, prevState) => {
        analytics.setUserId(action.result._id);
        isAppTrackingAuthorized(prevState) && analytics.setUserProperties({ email: action.result.email });
    },
    [api.workspaces.retrieve.successType]: (action) => {
        analytics.setUserProperties({ workspaceId: action.result._id, plan: action.result.billing.plan });
    },
    [checkout.applyCoupon.type]: (action) => {
        analytics.trackEvent('checkout.applyCoupon', { coupon: action.payload.coupon });
    },
    [checkout.removeCoupon.type]: () => {
        analytics.trackEvent('checkout.removeCoupon');
    },
    [checkout.changeCountry.type]: (action) => {
        analytics.trackEvent('checkout.changeCountry', action.payload);
    },
    [checkout.close.type]: () => {
        analytics.trackEvent('checkout.close');
    },
    [checkout.complete.type]: () => {
        analytics.trackEvent('checkout.complete');
    },
    [checkout.completePayment.type]: (action) => {
        analytics.trackEvent('checkout.completePayment', action.payload);
    },
    [checkout.load.type]: (action) => {
        analytics.trackEvent('checkout.load', action.payload);
    },
    [checkout.error.type]: (action) => {
        analytics.trackEvent('checkout.error', action.payload);
    },
    [checkout.selectPaymentMethod.type]: (action) => {
        analytics.trackEvent('checkout.selectPaymentMethod', action.payload);
    },
    [api.blocks.create.successType]: (action) => {
        analytics.trackEvent('Create block', { kind: action.result.kind });
    },
    [api.blocks.update.successType]: (action) => {
        analytics.trackEvent('Update block', { kind: action.result.kind });
    },
    [api.blocks.delete.successType]: (action, prevState) => {
        const website = prevState.websites.find(sift({ _id: action.websiteId }));
        const page = website?.pages.find(sift({ _id: action.pageId }));
        const block = page?.blocks.find(sift({ _id: action.blockId }));

        analytics.trackEvent('Delete block', { kind: block?.kind });
    },
    [api.blocks.deleteMany.successType]: (action, prevState) => {
        const website = prevState.websites.find(sift({ _id: action.websiteId }));
        const page = website?.pages.find(sift({ _id: action.pageId }));
        const blocks = page?.blocks.filter(sift({ _id: { $in: action.payload.blockId } })) || [];
        const uniqueKinds = [...new Set(blocks.map(({ kind }) => kind))];

        analytics.trackEvent('Delete many blocks', { kinds: uniqueKinds });
    },
    [api.websites.create.successType]: () => {
        analytics.trackEvent('Create website');
    },
    [api.websites.scaffold.successType]: () => {
        analytics.trackEvent('Create website');
    },
    [api.websites.delete.successType]: () => {
        analytics.trackEvent('Delete website');
    },
    [api.websites.domain.update.successType]: () => {
        analytics.trackEvent('domain.connect');
    },
    [api.websites.domain.delete.successType]: () => {
        analytics.trackEvent('domain.disconnect');
    },
    [api.products.create.successType]: () => {
        analytics.trackEvent('products.create');
    },
    [api.products.delete.successType]: () => {
        analytics.trackEvent('products.delete');
    },
    [api.products.update.successType]: () => {
        analytics.trackEvent('products.update');
    },
};

export const gtmMiddleware = (store) => (next) => (action) => {
    try {
        const handler = eventsMap[action.type];
        if (handler) {
            handler(action, store.getState());
        }
    } catch (error) {
        console.error(error);
        window.Sentry?.captureException(error);
    }
    return next(action);
};
